"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import {
  createContext,
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
} from "react";

import { cx } from "./cx";
import styles from "./tabs.module.css";

export const Tabs = TabsPrimitive.Root;

type TabVariant = "underline" | "pill";

const TabsVariantContext = createContext<TabVariant>("underline");

export const TabsList = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabVariant;
  }
>(({ className, variant = "underline", ...props }, ref) => {
  const variantCls =
    variant === "pill" ? styles.listPill : styles.listUnderline;
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        ref={ref}
        className={cx(styles.list, variantCls, className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
});
TabsList.displayName = "TabsList";

export const TabsTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    variant?: TabVariant;
  }
>(({ className, variant, ...props }, ref) => {
  const ctxVariant = useContext(TabsVariantContext);
  const effective = variant ?? ctxVariant;
  const variantCls =
    effective === "pill" ? styles.triggerPill : styles.triggerUnderline;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cx(variantCls, className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cx(styles.content, className)}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";
